import packageJson from "../package.json";
import type { components, operations } from "@generated/openapi";
import type * as websocket from "@generated/websocket";
import type { MakeRequired } from "@lib/utils/MakeRequired";
import type React from "react";

export const SDLABS_CLIENT_VERSION = packageJson.version; // TODO: Move to a better location as this is not a type.

export type CSVParseInput = components["schemas"]["ParseCSVInput"];
export type CSVParseOutput = components["schemas"]["CSVParseOutput"];
export type CSVParsingResult = components["schemas"]["CSVParsingResult"];
export type User = components["schemas"]["User"];
export type ParameterType = components["schemas"]["ParameterTypeEnum"];
export type Experiment = components["schemas"]["Experiment"];
export type ExperimentId = Experiment["id"];
export type ExperimentListItem = components["schemas"]["ExperimentListItem"];
export type ExperimentCreateInput = components["schemas"]["ExperimentCreateInput"];
export type ExperimentDraftCreateInput = components["schemas"]["ExperimentDraftCreateInput"];
export type ExperimentInputParameterMappingCreateInput =
  components["schemas"]["ExperimentInputParameterMappingCreateInput"];
export type ExperimentInputMeasurementMappingCreateInput =
  components["schemas"]["ExperimentInputMeasurementMappingCreateInput"];
export type ExperimentInputDatasetMappingCreateInput =
  components["schemas"]["ExperimentInputDatasetMappingCreateInput"];
export type ExperimentInputParameterMapping = components["schemas"]["ExperimentInputParameterMapping"];
export type ExperimentInputMeasurementMapping = components["schemas"]["ExperimentInputMeasurementMapping"];
export type ExperimentInputDatasetMapping = components["schemas"]["ExperimentInputDatasetMapping"];
export type PatchedExperimentCreateInput = components["schemas"]["PatchedExperimentCreateInput"];
export type Parameter = components["schemas"]["Parameter"];
export type ParameterCreateInput = components["schemas"]["ParameterCreateInput"];
export type ParameterProperty = components["schemas"]["ParameterProperty"];
export type ParameterPropertyCreateInput = components["schemas"]["ParameterPropertyCreateInput"];
export type ParameterCategoryPropertyValueCreateInput =
  components["schemas"]["ParameterCategoryPropertyValueCreateInput"];
export type ParameterCategoryCreateInput = components["schemas"]["ParameterCategoryCreateInput"];
export type ParameterCategory = components["schemas"]["ParameterCategory"];
export type NumericalParameterCreateInput = components["schemas"]["NumericalParameterCreateInput"];
export type NumericalParameter = components["schemas"]["NumericalParameter"];
export type CategoricalParameter = components["schemas"]["CategoricalParameter"];
export type CategoricalParameterCreateInput = components["schemas"]["CategoricalParameterCreateInput"];
export type Measurement = components["schemas"]["Measurement"];
export type MeasurementId = Measurement["id"];
export type MeasurementCreateInput = components["schemas"]["MeasurementCreateInput"];
export type BatchConfigCreateInput = components["schemas"]["BatchConfigCreateInput"];
export type DatasetCreateInput = components["schemas"]["DatasetCreateInput"];
export type DatasetParameterCreateInput = components["schemas"]["DatasetParameterCreateInput"];
export type DatasetMeasurementCreateInput = components["schemas"]["DatasetMeasurementCreateInput"];
export type Dataset = components["schemas"]["Dataset"];
export type DatasetListItem = components["schemas"]["DatasetListItem"];
export type DatasetId = Dataset["id"];
export type DatapointCreateInput = components["schemas"]["DatapointCreateInput"];
export type DatapointParameterValueCreateInput = components["schemas"]["DatapointParameterValueCreateInput"];
export type DatapointMeasurementValueCreateInput = components["schemas"]["DatapointMeasurementValueCreateInput"];
export type AlgorithmCreateInput = components["schemas"]["AlgorithmCreateInput"];
export type AlgorithmFunctionEnum = components["schemas"]["FunctionEnum"];
export type FalconConfigCreateInput = components["schemas"]["FalconConfigCreateInput"];
export type ConstraintCreateInput = components["schemas"]["ConstraintCreateInput"];
export type ExclusionConstraintDefinitionCreateInput =
  components["schemas"]["ExclusionConstraintDefinitionCreateInput"];
export type ExclusionConstraintBoundsCreateInput = components["schemas"]["ExclusionConstraintBoundsCreateInput"];
export type LinearConstraintOperatorEnum = components["schemas"]["OperatorEnum"];
export type LinearConstraintDefinitionCreateInput = components["schemas"]["LinearConstraintDefinitionCreateInput"];
export type LinearConstraintCreateInput = components["schemas"]["LinearConstraintCreateInput"];
export type LinearConstraint = components["schemas"]["LinearConstraint"];
export type SubsetConstraintOperatorEnum = components["schemas"]["OperatorEnum"];
export type SubsetConstraintDefinitionCreateInput = components["schemas"]["SubsetConstraintDefinitionCreateInput"];
export type SubsetConstraintCreateInput = components["schemas"]["SubsetConstraintCreateInput"];
export type SubsetConstraint = components["schemas"]["SubsetConstraint"];
export type ObjectiveCreateInput = components["schemas"]["ObjectiveCreateInput"];
export type Objective = components["schemas"]["Objective"];
export type ParetoOptimizationCreateInput = components["schemas"]["ParetoOptimizationCreateInput"];
export type ParetoOptimization = components["schemas"]["ParetoOptimization"];
export type ChimeraCreateInput = components["schemas"]["ChimeraCreateInput"];
export type Chimera = components["schemas"]["Chimera"];
export type GoalEnum = components["schemas"]["GoalEnum"];
export type ParameterId = Parameter["id"];
export type Datapoint = components["schemas"]["Datapoint"];
export type DatapointId = Datapoint["id"];
export type DatapointBulkUpdateInput = components["schemas"]["DatapointBulkUpdateInput"];
export type WeightedSum = components["schemas"]["WeightedSum"];
export type WeightedSumCreateInput = components["schemas"]["WeightedSumCreateInput"];
export type ExpertContextCreateInput = components["schemas"]["ExpertContextCreateInput"];
export type ExpertContext = components["schemas"]["ExpertContext"];
export type PlumaConfig = components["schemas"]["PlumaConfig"];
export type SubmitMeasurementsInput = components["schemas"]["SubmitMeasurementsInput"];
export type SubmitMeasurementsSingleDatapointInput = components["schemas"]["SubmitMeasurementsSingleDatapointInput"];
export type SubmitMeasurementsParameterInput = components["schemas"]["SubmitMeasurementsParameterInput"];
export type Constraint = components["schemas"]["Constraint"];
export type ConstraintId = Constraint["id"];
export type ExclusionConstraintCreateInput = components["schemas"]["ExclusionConstraintCreateInput"];
export type ExclusionConstraint = components["schemas"]["ExclusionConstraint"];
export type ConditionalExclusionConstraintCreateInput =
  components["schemas"]["ConditionalExclusionConstraintCreateInput"];
export type ConditionalExclusionConstraintDefinitionCreateInput =
  components["schemas"]["ConditionalExclusionConstraintDefinitionCreateInput"];
export type ConditionalExclusionConstraint = components["schemas"]["ConditionalExclusionConstraint"];
export type DatasetSpace = components["schemas"]["DatasetSpace"];
export type ExperimentSearchResult = components["schemas"]["ExperimentSearchResult"];
export type DatasetSearchResult = components["schemas"]["DatasetSearchResult"];
export type SearchResults = components["schemas"]["SearchResults"];
export type ParallelCoordinatesResponse = components["schemas"]["ParallelCoordinatesResponse"];
export type ExperimentParallelCoordinatesResponse = components["schemas"]["ExperimentParallelCoordinatesResponse"];
export type DesignSpace = components["schemas"]["DesignSpace"];
export type ResearchSession = components["schemas"]["ResearchSession"];
export type ResearchSessionStatus = ResearchSession["status"];
export type ResearchReportListItem = components["schemas"]["ResearchReportListItem"];
export type ResearchReport = components["schemas"]["ResearchReport"];

// https://linear.app/atinary-tech/issue/DEV-1602/agree-on-backend-error-types-used-in-frontend
export type GenericErrorResponse = components["schemas"]["GenericErrorResponse"];
export type GenericErrorWithSuggestionResponse = components["schemas"]["GenericErrorWithSuggestionResponse"];

// Experiment configuration deletion error types
export type RelatedExperiment = components["schemas"]["RelatedExperiment"];
export type ExperimentConfigurationDeletionErrorResponse =
  components["schemas"]["ExperimentConfigurationDeletionErrorResponse"];

// Extract status type from Experiment schema to not use hardcoded enum name
export type ExperimentStatus = Experiment["status"];
export type ParameterFormInput = MakeRequired<ParameterCreateInput, "id">;
export type UserDetails = components["schemas"]["UserDetails"];
export type UserRoleEnum = components["schemas"]["RoleEnum"];
export type Team = components["schemas"]["Team"];
export type CortexWarning = components["schemas"]["CortexWarning"];
export type CortexWarningType = components["schemas"]["WarningTypeEnum"];
export type CortexError = components["schemas"]["CortexError"];
export type CortexErrorType = components["schemas"]["ErrorTypeEnum"];
export type PaginatedExperimentListItem = components["schemas"]["PaginatedExperimentListItem"];
export type Run = components["schemas"]["Run"];
export type ExperimentRun = components["schemas"]["ExperimentRun"];
export type ListExperimentQueryParams = operations["list_experiments"]["parameters"]["query"];
export type ListDatasetQueryParams = operations["list_datasets"]["parameters"]["query"];
export type PaginatedDatapoint = components["schemas"]["PaginatedDatapoint"];
export type MeasurementStat = components["schemas"]["MeasurementStat"];
export type OptimizationPhase = components["schemas"]["OptimizationPhaseEnum"];
export type OptimizationDifficulty = components["schemas"]["OptimizationDifficulty"];
export type OptimizationDifficultyRating = components["schemas"]["OptimizationDifficultyRatingEnum"];
export type LandscapeInput = components["schemas"]["LandscapeInput"];
export type LandscapeResponse = components["schemas"]["LandscapeResponse"];
export type ProfilerInput = components["schemas"]["ProfilerInput"];
export type ProfilerResponse = components["schemas"]["ProfilerResponse"];
export type APIKeyCreateResponse = components["schemas"]["APIKeyCreateResponse"];
export type PredictionExplanationInput = components["schemas"]["PredictionExplanationInput"];
export type PredictionInput = components["schemas"]["PredictionInput"];
export type PredictionExplanationResponse = components["schemas"]["PredictionExplanationResponse"];
export type ModelStatusResponse = components["schemas"]["ModelStatusResponse"];

export type AgentSessionFeedbackRating = components["schemas"]["AgentSessionFeedbackSubmitRatingEnum"];
export type AgentSessionId = components["schemas"]["AgentSessionFeedbackSubmit"]["agent_session_id"];

// Metadata for one uploaded agent-session document; the chat UI reads the `filename` for
// chips. Nothing is sent on the WS prompt — the agent resolves the session's document set
// server-side, and bytes never leave the backend.
export type AgentSessionDocument = components["schemas"]["AgentSessionDocument"];

// Browser-facing projection of a Strands SessionMessage. `content` is typed as `unknown`
// because the backend serializer holds the JSON payload — narrow to a content-block list
// in the frontend translator.
export type ChatHistoryMessage = components["schemas"]["ChatMessage"];
export type ChatHistoryMessageList = components["schemas"]["ChatMessageList"];

// WebSocket message types (from generated AsyncAPI schema)
export type AuthenticationMessage = websocket.AuthenticationMessage;
export type ActivityCreatedMessage = websocket.ActivityCreatedMessage;
export type AgentErrorMessage = websocket.AgentErrorMessage;
export type AgentReadyMessage = websocket.AgentReadyMessage;
export type AgentErrorPayload = websocket.AgentErrorPayload;
export type AgentPingMessage = websocket.AgentPingMessage;
export type AgentPongMessage = websocket.AgentPongMessage;
export type AgentPromptMessage = websocket.AgentPromptMessage;
export type AgentPromptPayload = websocket.AgentPromptPayload;
export type AgentResponseChunkMessage = websocket.AgentResponseChunkMessage;
export type AgentResponseChunkPayload = websocket.AgentResponseChunkPayload;
export type AgentStopMessage = websocket.AgentStopMessage;
export type AgentStopPayload = websocket.AgentStopPayload;
export type AgentExtrasMessage = websocket.AgentExtrasMessage;
export type AgentExtrasPayload = websocket.AgentExtrasPayload;
export type AgentTurnDoneMessage = websocket.AgentTurnDoneMessage;
export type AgentTurnDonePayload = websocket.AgentTurnDonePayload;
export type AgentTurnStartedMessage = websocket.AgentTurnStartedMessage;
export type AgentTurnStartedPayload = websocket.AgentTurnStartedPayload;
export type AgentToolUseMessage = websocket.AgentToolUseMessage;
export type AgentToolUsePayload = websocket.AgentToolUsePayload;
export type AgentToolApprovalRequiredMessage = websocket.AgentToolApprovalRequiredMessage;
export type AgentToolApprovalRequiredPayload = websocket.AgentToolApprovalRequiredPayload;
export type AgentToolApprovalResponseMessage = websocket.AgentToolApprovalResponseMessage;
export type AgentToolApprovalResponsePayload = websocket.AgentToolApprovalResponsePayload;
export type ToolApprovalRequest = websocket.ToolApprovalRequest;
export type ToolApprovalDecision = websocket.ToolApprovalDecision;
export type LandscapeChartMessage = websocket.LandscapeChartMessage;
export type LandscapeChartPayload = websocket.LandscapeChartPayload;
export type ResearchProgressMessage = websocket.ResearchProgressMessage;
export type ResearchProgressPayload = websocket.ResearchProgressPayload;
export type NotificationPingMessage = websocket.NotificationPingMessage;
export type NotificationPongMessage = websocket.NotificationPongMessage;
export type PredictionExplanationChartMessage = websocket.PredictionExplanationChartMessage;
export type PredictionExplanationChartPayload = websocket.PredictionExplanationChartPayload;
export type ResearchReportReadyMessage = websocket.ResearchReportReadyMessage;
export type ResearchReportReadyPayload = websocket.ResearchReportReadyPayload;
export type ReservedStatus = websocket.ReservedStatus;
export type ResearchStatusMessage = websocket.ResearchStatusMessage;
export type ResearchStatusPayload = websocket.ResearchStatusPayload;

// Structured response items for agent messages
export type QuestionOpenItem = websocket.QuestionOpenItem;
export type QuestionOption = websocket.QuestionOption;
export type QuestionSelectMultiItem = websocket.QuestionSelectMultiItem;
export type QuestionSelectSingleItem = websocket.QuestionSelectSingleItem;
export type ResourceLink = websocket.ResourceLink;
export type ResourceLinkItem = websocket.ResourceLinkItem;

// Override ExperimentUpdatePayload to use ExperimentStatus enum from OpenAPI
export type ExperimentUpdatePayload = {
  id: ExperimentId;
  name: string;
  status: ExperimentStatus;
};

// Override ExperimentUpdateMessage to use corrected payload
export type ExperimentUpdateMessage = {
  action: "experiment.update";
  payload: ExperimentUpdatePayload;
};

// Union of all WebSocket messages
export type WebsocketMessage =
  | AuthenticationMessage
  | ActivityCreatedMessage
  | AgentErrorMessage
  | AgentExtrasMessage
  | AgentPingMessage
  | AgentPongMessage
  | AgentPromptMessage
  | AgentResponseChunkMessage
  | AgentStopMessage
  | AgentToolUseMessage
  | AgentTurnDoneMessage
  | AgentTurnStartedMessage
  | ExperimentUpdateMessage
  | LandscapeChartMessage
  | ResearchProgressMessage
  | NotificationPingMessage
  | NotificationPongMessage
  | PredictionExplanationChartMessage
  | ResearchReportReadyMessage
  | ResearchStatusMessage;

export type WebsocketAction = WebsocketMessage["action"];
export type MessageOfAction<TAction extends WebsocketAction> = Extract<WebsocketMessage, { action: TAction }>;

// Base type for valibot schema validation - all WebsocketMessage variants satisfy this
export type BaseWebsocketMessage = {
  action: WebsocketAction;
  payload?: unknown;
};

// Union of agent client-to-server messages
export type AgentClientMessage =
  AgentPromptMessage | AgentPingMessage | AgentStopMessage | AgentToolApprovalResponseMessage;

// Union of agent server-to-client messages
export type AgentServerMessage =
  | AuthenticationMessage
  | AgentResponseChunkMessage
  | AgentExtrasMessage
  | AgentTurnStartedMessage
  | AgentTurnDoneMessage
  | AgentToolUseMessage
  | AgentToolApprovalRequiredMessage
  | AgentErrorMessage
  | AgentPongMessage
  | AgentReadyMessage;

export type AgentServerAction = AgentServerMessage["action"];

// Base type for valibot schema validation - all AgentServerMessage variants satisfy this
export type BaseAgentServerMessage = {
  action: AgentServerAction;
  payload?: unknown;
};

export type ExperimentFormInput = Omit<ExperimentCreateInput, "parameters"> & {
  parameters: ParameterFormInput[];
  // store design_space locally to avoid fetching it, as it is returned by the Experiment serializer
  // done in experimentToFormInput function in useExperimentDraft.ts by spreading the experiment object
  design_space?: DesignSpace | null;
};

declare module "react/jsx-runtime" {
  namespace JSX {
    interface IntrinsicElements {
      "wa-icon": React.DetailedHTMLProps;
    }
  }
}
